<template>
    <div v-if="simulation">
        <top-navbar :breadcrumb="breadcrumb"></top-navbar>

        <div class="container-fluid mt--6">
            <div class="row">
                <div class="d-none d-lg-block col-lg-4">

                    <user-card :user="learner"
                        :cohort="registration.cohort"
                        :groups="groups"
                        @openCohort="openCohort"
                        @openGroup="openGroup">
                    </user-card>

                    <session
                        :session="session"
                        @updated="session = $event"
                        :learner="learner"
                        :learners="learners"
                        :edit="true"
                        v-if="session"
                    ></session>

                    <status :simulation="simulation"
                        :registration="registration"
                        :learner="learner"
                        :competencies-filled="competenciesFilled"
                        @openGeneralComment="openGeneralComment"
                        @openCompetencies="openCompetencies"
                    >
                    </status>

                    <revisions :simulation="simulation">
                    </revisions>
                </div>
                <div class="col-lg-8">
                    <simulation ref="simulation"
                        :simulation="simulation"
                        :simulations="simulations"
                        :registration="registration"
                        :groups="groups"
                        :allow-comment-edit="true"
                        @change="reloadSimulation">
                    </simulation>

                    <div class="d-lg-none">
                        <user-card :user="learner"
                            :cohort="registration.cohort"
                            :groups="groups"
                            :compact="true"
                            @openCohort="openCohort"
                            @openGroup="openGroup">
                        </user-card>

                        <session
                            :session="session"
                            @updated="session = $event"
                            :learner="learner"
                            :learners="learners"
                            :compact="true"
                            :edit="true"
                            v-if="session"
                        >
                        </session>

                        <status :simulation="simulation"
                            :registration="registration"
                            :learner="learner"
                            :competencies-filled="competenciesFilled"
                            :compact="true"
                            @openGeneralComment="openGeneralComment"
                            @openCompetencies="openCompetencies"
                        >
                        </status>

                        <revisions :simulation="simulation">
                        </revisions>
                    </div>

                    <tabs class="tabs" ref="simulation-tabs"
                        tabNavClasses="nav-fill flex-column flex-sm-row nav-wrapper pb-3"
                        tabContentClasses=""
                        v-if="!simulation.assessment"
                    >

                        <tab-pane class="mb-0" id="simulation"
                            v-if="!simulation.assessment"
                        >
                            <span slot="title">
                                {{ $t('common.simulation') }}
                            </span>

                            <feedbacks
                                :registration="registration"
                                :simulation="simulation"
                                :session="session"
                                :learner="learner"
                                :competencies="competencies"
                                :edit="true"
                            >
                            </feedbacks>

                            <div class="text-center mt-4 mb-4" v-if="goGround">
                                <small>
                                    <i class="fas fa-plane-departure mr-1"></i>
                                    {{ $t('degrees.go-ground-after-session') }}
                                </small>
                            </div>
                        </tab-pane>

                        <tab-pane class="card pb-1 pt-2" id="grid1"
                            v-if="simulation.grid1 && !groundCompetenciesDisabled(registration.degree.simulation_role_1)"
                        >
                            <span slot="title">
                                {{ $t('common.competencies') }}
                                <span v-if="registration.degree.simulation_role_2 && simulation.grid2">
                                    : {{ registration.degree.simulation_role_1_name[registration.degree.lang] }}
                                </span>
                            </span>

                            <competencies
                                :simulation="simulation"
                                :registration="registration"
                                :rating-options="ratingOptions"
                                :num="1"
                                @change="ratings1Changed"
                                @submit="submitRatings">
                            </competencies>

                            <div class="text-center mt-4 mb-4">

                                <base-switch class="mb-3"
                                    :value="goGround"
                                    @input="goGroundChanged($event)"
                                    :label="$t('degrees.go-ground-after-session')"
                                    :inline-label="true"
                                    v-if="displayGoGround"
                                ></base-switch>

                            </div>
                        </tab-pane>

                        <tab-pane class="card pb-1 pt-2" id="grid2"
                            v-if="simulation.grid2 && registration.degree.simulation_role_2 && !groundCompetenciesDisabled(registration.degree.simulation_role_2)"
                        >
                            <span slot="title">
                                {{ $t('common.competencies') }}:
                                {{ registration.degree.simulation_role_2_name[registration.degree.lang] }}
                            </span>

                            <competencies
                                :simulation="simulation"
                                :registration="registration"
                                :rating-options="ratingOptions"
                                :num="2"
                                @change="ratings2Changed"
                                @submit="submitRatings">
                            </competencies>

                            <div class="text-center mt-4 mb-4">

                                <base-switch class="mb-3"
                                    :value="goGround"
                                    @input="goGroundChanged($event)"
                                    :label="$t('degrees.go-ground-after-session')"
                                    :inline-label="true"
                                    v-if="displayGoGround"
                                ></base-switch>

                            </div>
                        </tab-pane>
                    </tabs>

                    <div class="card pb-1 pt-3" id="assessment"
                        v-if="simulation.assessment"
                    >
                        <assessment
                            :simulation="simulation"
                            :registration="registration"
                            :rating-options="ratingOptions"
                            :domains="domains"
                            @change="handleAssessmentChange">
                        </assessment>

                        <div class="text-center mb-4">

                            <base-button type="primary"
                                :loading="recording"
                                :disabled="!assessmentChanged"
                                @click.prevent="submitAssessment"
                            >
                                {{ $t('common.save') }}
                            </base-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import UserCard from '../auth/components/UserCard'
    import Simulation from './learner/Simulation';
    import Competencies from './learner/SimulationCompetenciesEdit';
    import Assessment from './learner/SimulationAssessmentEdit';
    import Status from './learner/SimulationStatus';
    import Revisions from './learner/SimulationRevisions';
    import Session from './learner/Session';
    import Feedbacks from '../log/common/Feedbacks';

    export default {

        components: {
            UserCard, Simulation, Competencies, Assessment, Status, Revisions, Feedbacks, Session
        },

        data() {
            return {
                learner: {},
                learners: null,
                groups: null,
                registration: null,
                simulation: null,
                simulations: null,
                session: null,
                ratingOptions: null,
                ratings: {},
                comments: {},
                comment: null,
                goGround: false,
                competencies1: null,
                competencies2: null,
                recording: false,
                competencies: null,
                ratingsChanged: false,
                assessmentChanged: false,
                initialGoGround: null,
                domains: null,
            }
        },

        beforeRouteEnter(to, from, next) {
            Vue.prototype.$loader.show()
            axios.get('/astral/api/front/dashboard/registrations/'+to.params.registrationId+'/simulations/'+to.params.simulationId+'/learners/'+to.params.learnerId)
            .then(resp => {
                if (resp.data.data.state.published || (!resp.data.data.session && !resp.data.data.assessment)) {
                    // Don't continue if the state is already published
                    // or if there is no session.
                    next({
                        name: 'dashboard-learner-simulation',
                        params: {
                            learnerId: to.params.learnerId,
                            registrationId: to.params.registrationId,
                            simulationId: to.params.simulationId,
                        }
                    });

                } else {

                    next(vm => {
                        vm.simulation = resp.data.data
                        vm.session = vm.simulation.session
                        vm.registration = resp.data.included.registration
                        vm.simulations = resp.data.included.simulations
                        vm.domains = resp.data.included.competency_domains
                        vm.learner = resp.data.included.learner
                        vm.learners = resp.data.included.learners
                        vm.groups = resp.data.included.groups
                        vm.ratingOptions = resp.data.included.rating_options
                        vm.competencies = resp.data.included.competencies
                        vm.goGround = vm.registration.state.meta
                            ? vm.registration.state.meta.go_ground == vm.simulation.id
                            : false
                        vm.initialGoGround = vm.goGround
                        vm.$permissions.setRights(resp.data.included.rights)
                        vm.$loader.hide()
                        //vm.rememberDomains()
                    })
                }
            })
            .catch(err => {
                if (err.response.status == 403) {
                    next({ name: 'unauthorized' });
                }
            });
        },

        watch: {
            // Transversal learners navigation causes change of learner ID without reloading the component.
            // So we need to refresh the component manually.
            '$route.params.learnerId': function (id) {
                axios.get('/astral/api/front/dashboard/registrations/'+this.registration.id+'/simulations/'+this.simulation.id+'/learners/'+this.$route.params.learnerId)
                .then(resp => {
                    if (resp.data.data.state.published || !resp.data.data.session) {

                        // Don't continue if the state is already published
                        // or if there is no session.
                        this.$router.push({
                            name: 'dashboard-learner-simulation',
                            params: {
                                learnerId: this.$route.params.learnerId,
                                registrationId: this.$route.params.registrationId,
                                simulationId: this.$route.params.simulationId,
                            }
                        });

                    } else {

                        this.simulation = resp.data.data
                        this.session = this.simulation.session
                        this.registration = resp.data.included.registration
                        this.simulations = resp.data.included.simulations
                        this.learner = resp.data.included.learner
                        this.groups = resp.data.included.groups
                        this.ratingOptions = resp.data.included.rating_options
                        this.competencies = resp.data.included.competencies
                        this.goGround = this.registration.state.meta
                            ? this.registration.state.meta.go_ground == this.simulation.id
                            : false
                        this.initialGoGround = this.goGround
                    }
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },
        },

        computed: {

            breadcrumb() {
                return [
                    {
                        name: this.learner.name,
                        route: '/astral/ui/dashboard/learners/' + this.$route.params.learnerId
                    },
                    {
                        name: this.registration.degree.name,
                        route: '/astral/ui/dashboard/registrations/' + this.$route.params.registrationId + '/learners/' + this.$route.params.learnerId
                    },
                    {
                        name: this.simulation.name,
                    },
                ]
            },

            competenciesFilled() {
                if (this.simulation.assessment) {
                    return this.assessmentFilled()
                } else {
                    return this.gridFilled(1) && (!this.registration.degree.simulation_role_2 || this.gridFilled(2))
                }
            },

            displayGoGround() {
                return this.registration.degree.go_ground && (
                    !this.registration.state.meta
                    || !this.registration.state.meta.go_ground
                    || this.registration.state.meta.go_ground == this.simulation.id
                )
            },
        },

        methods: {

            openGeneralComment() {
                if (this.simulation.assessment) {
                    return
                }
                this.$refs['simulation'].editComment()
            },

            openCompetencies() {
                if (this.simulation.assessment) {
                    return
                }
                if (this.simulation.grid1 && !this.groundCompetenciesDisabled(this.registration.degree.simulation_role_1)) {
                    this.$refs['simulation-tabs'].findAndActivateTab('grid1')
                } else if (this.simulation.grid2 && this.registration.degree.simulation_role_2 && !this.groundCompetenciesDisabled(this.registration.degree.simulation_role_2)) {
                    this.$refs['simulation-tabs'].findAndActivateTab('grid2')
                }
            },

            groundCompetenciesDisabled(simulationRole) {
                if (simulationRole !== 'sol'
                    || !this.registration.degree.go_ground
                    || !this.registration.state.meta
                    || !this.registration.state.meta.go_ground
                ) {
                    return false
                }
                let goGround = false
                let currentSimFound = false
                this.simulations.forEach(simulation => {
                    if (simulation.id == this.simulation.id) {
                        currentSimFound = true
                    }
                    if (currentSimFound) {
                        // Don't continue after the current simulation. No break with ES6.
                        return
                    }
                    if (this.registration.state.meta.go_ground == simulation.id) {
                        goGround = true
                    }
                })
                return goGround
            },

            openCohort() {
                this.$router.push({
                    name: 'dashboard-cohort-simulation',
                    params: {
                        cohortId: this.registration.cohort.id,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.$route.params.simulationId
                    }
                });
            },

            openGroup() {
                this.$router.push({
                    name: 'dashboard-group-simulation',
                    params: {
                        groupId: this.groups[0].id,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.$route.params.simulationId
                    }
                });
            },

            assessmentFilled() {

                if (!this.simulation || !this.simulation.state || !this.simulation.state.domains || !this.simulation.state.domain_comments) {
                    return false
                }

                // Ratings
                const filledRatings = Object.keys(this.simulation.state.domains)
                if (filledRatings.length < this.domains.length) {
                    return false
                }
                const allRatings = filledRatings.reduce((filled, id) => {
                    return filled && parseInt(this.simulation.state.domains[id]) >= 0
                }, true)

                // Comments
                const filledComments = Object.keys(this.simulation.state.domain_comments)
                if (filledComments.length < this.domains.length) {
                    return false
                }
                const allComments = filledComments.reduce((filled, id) => {
                    return filled && this.simulation.state.domain_comments[id]
                }, true)

                return allComments //&& allRatings
            },

            gridFilled(num) {

                if (!this.simulation['grid'+num]) {
                    return true
                }

                const simulationRole = this.registration.degree['simulation_role_'+num]
                if (this.groundCompetenciesDisabled(simulationRole)) {
                    return true
                }

                if (!this.simulation || !this.simulation.state || !this.simulation.state.competencies) {
                    return false
                }

                const competencies = this['competencies'+num]
                if (!competencies) {
                    return false
                }

                const state = this.simulation.state.competencies[simulationRole]
                if (!state) {
                    return false
                }
                
                const filledCompetencies = Object.keys(state)
                if (filledCompetencies.length < competencies.length) {
                    return false
                }

                const allFilled = filledCompetencies.reduce((filled, id) => {
                    return filled && parseInt(state[id]) >= 0
                }, true)

                return allFilled
            },

            gridRatingsChanged(num) {

                const competencies = this['competencies'+num]
                if (!competencies) {
                    return false
                }

                let recordedRatings = null
                if (this.simulation && this.simulation.state && this.simulation.state.competencies) {
                    recordedRatings = this.simulation.state.competencies[this.registration.degree['simulation_role_'+num]]
                }
                if (!recordedRatings) {
                    recordedRatings = {}
                    competencies.forEach(competency => {
                        recordedRatings[competency.id] = -1
                    });
                }

                let recordedComments = null
                if (this.simulation && this.simulation.state && this.simulation.state.competency_comments) {
                    recordedComments = this.simulation.state.competency_comments[this.registration.degree['simulation_role_'+num]]
                }
                if (!recordedComments) {
                    recordedComments = {}
                    competencies.forEach(competency => {
                        recordedComments[competency.id] = null
                    });
                }
                
                let currentRatings = this.ratings[this.registration.degree['simulation_role_'+num]]
                if (!currentRatings) {
                    currentRatings = {}
                }

                let currentComments = this.comments[this.registration.degree['simulation_role_'+num]]
                if (!currentComments) {
                    currentComments = {}
                }

                return JSON.stringify(recordedRatings) !== JSON.stringify(currentRatings)
                    || JSON.stringify(recordedComments) !== JSON.stringify(currentComments)
            },

            checkRatingsChange() {
                this.ratingsChanged = this.gridRatingsChanged(1) || (this.registration.degree.simulation_role_2 && this.gridRatingsChanged(2))
            },

            ratings1Changed(event) {
                this.ratings[this.registration.degree.simulation_role_1] = event.ratings
                this.comments[this.registration.degree.simulation_role_1] = event.comments
                this.competencies1 = event.competencies
                this.checkRatingsChange()
            },

            ratings2Changed(event) {
                this.ratings[this.registration.degree.simulation_role_2] = event.ratings
                this.comments[this.registration.degree.simulation_role_2] = event.comments
                this.competencies2 = event.competencies
                this.checkRatingsChange()
            },

            goGroundChanged(value) {
                this.goGround = value
                this.submitRatings()
            },

            handleAssessmentChange(event) {
                this.ratings = event.ratings
                this.comments = event.comments
                this.comment = event.comment
                this.checkAssessmentChange()
            },

            checkAssessmentChange() {
                
                let recordedRatings = null
                if (this.simulation && this.simulation.state && this.simulation.state.domains) {
                    recordedRatings = this.simulation.state.domains
                }
                if (!recordedRatings) {
                    recordedRatings = {}
                    this.domains.forEach(domain => {
                        recordedRatings[domain.id] = -1
                    });
                }

                let recordedComments = null
                if (this.simulation && this.simulation.state && this.simulation.state.domain_comments) {
                    recordedComments = this.simulation.state.domain_comments
                }
                if (!recordedComments) {
                    recordedComments = {}
                    this.domains.forEach(domain => {
                        recordedComments[domain.id] = null
                    });
                }

                let recordedComment = null
                if (this.simulation && this.simulation.state && this.simulation.state.comment) {
                    recordedComment = this.simulation.state.comment
                }
                if (!recordedComment) {
                    recordedComment = null
                }

                this.assessmentChanged = JSON.stringify(recordedRatings) !== JSON.stringify(this.ratings)
                    || JSON.stringify(recordedComments) !== JSON.stringify(this.comments)
                    || recordedComment !== this.comment
            },

            submitAssessment(event) {
                this.recording = true
                axios.post('/astral/api/front/dashboard/registrations/'+this.$route.params.registrationId+'/simulations/'+this.$route.params.simulationId+'/learners/'+this.$route.params.learnerId+'/assessment', {
                    domains: this.ratings,
                    domain_comments: this.comments,
                    comment: this.comment,
                })
                .then(resp => {
                    this.recording = false
                    this.reloadSimulation()
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            submitRatings() {
                this.recording = true
                axios.post('/astral/api/front/dashboard/registrations/'+this.$route.params.registrationId+'/simulations/'+this.$route.params.simulationId+'/learners/'+this.$route.params.learnerId+'/competencies', {
                    competencies: this.ratings,
                    competency_comments: this.comments,
                    go_ground: this.goGround
                })
                .then(resp => {
                    this.recording = false
                    this.reloadSimulation()
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            reloadSimulation() {
                axios.get('/astral/api/front/dashboard/registrations/'+this.$route.params.registrationId+'/simulations/'+this.$route.params.simulationId+'/learners/'+this.$route.params.learnerId)
                .then(resp => {
                    this.simulation = resp.data.data
                    this.initialGoGround = this.goGround
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            }
        }
    }
</script>

<style scoped>

    .tabs >>> .nav-pills {
        padding-top: 0;
        padding-bottom: 1.5rem;
    }

</style>
